import { isRegionTimezone } from "@/shared/lib/zonedTime";
import { createOrganisationSchema } from "entities/organisation/organisation.schema";
import { isIBANValid } from "swissqrbill/utils";
import { z } from "zod";

/**
 * Schema for practice onboarding form (multi-step wizard)
 * Step 1: Basic Info (required)
 * Step 2: Contact (optional)
 * Step 3: Address (optional)
 * Step 4: Business (optional)
 */
export const practiceOnboardingSchema = z.object({
  // Step 1: Basic Info (required)
  organisationName: createOrganisationSchema.shape.name,
  practiceName: z.string().min(1, "Practice name is required"),

  // Step 2: Contact Info (all optional)
  phone: z.string().nullable().optional(),
  email: z
    .string()
    .email("Please enter a valid email address")
    .nullable()
    .optional()
    .or(z.literal("")),
  website: z
    .string()
    .url("Please enter a valid URL")
    .nullable()
    .optional()
    .or(z.literal("")),

  // Step 3: Address (all optional) - includes Google Places metadata
  street: z.string().nullable().optional(),
  house_no: z.string().nullable().optional(),
  city: z.string().nullable().optional(),
  postal_code: z.string().nullable().optional(),
  country_code: z.string().nullable().optional(),
  // Google Places metadata (set by AddressAutocomplete)
  address_place_id: z.string().nullable().optional(),
  address_formatted: z.string().nullable().optional(),
  address_lat: z.number().nullable().optional(),
  address_lng: z.number().nullable().optional(),

  // Step 4: Business Info (all optional)
  legal_name: z.string().nullable().optional(),
  tax_id: z.string().nullable().optional(),
  iban: z
    .string()
    .nullable()
    .optional()
    .refine(
      (val) => {
        if (!val || val.trim() === "") return true;
        // Validate IBAN checksum using swissqrbill
        return isIBANValid(val.replace(/\s/g, ""));
      },
      { message: "Invalid IBAN format" }
    ),
  vat_rate: z.string().nullable().optional(), // Stored as string, transformed to decimal on backend
  currency: z.enum(["CHF", "EUR", "USD"]).nullable().optional(),
  // Timezone. Must be a Region/City identifier — abbreviations like "EST" are
  // permanent fixed offsets that never observe daylight saving.
  tzid: z
    .string()
    .nullable()
    .optional()
    .refine((val) => !val || isRegionTimezone(val), {
      message: "Select a valid timezone",
    }),
});

export type PracticeOnboardingData = z.infer<typeof practiceOnboardingSchema>;
